import * as tslib_1 from "tslib";
import * as d3 from "d3";
import selectTimeRange from "@/components/selectTimeRange.vue";
import loader from "@/components/loader.vue";
import dataService from "@/services/data";
import noDataOverlay from "@/components/noDataOverlay.vue";
import { t } from "@/helpers/i18n";
import { Getter, Mutation } from "vuex-class";
import { Vue, Component, Watch } from "vue-property-decorator";
const am = t("am");
const pm = t("pm");
const dr = t("dayRange");
let widget = class widget extends Vue {
    constructor() {
        super(...arguments);
        this.loading = true;
        this.width = 400;
        this.height = 340;
        this.margin = { top: 30, right: 80, bottom: 60, left: 0 };
        this.buckets = 9;
        this.days = [dr.su.s2, dr.mo.s2, dr.tu.s2, dr.we.s2, dr.th.s2, dr.fr.s2, dr.sa.s2];
        this.times = [
            `12 ${am}`,
            "",
            `2 ${am}`,
            "",
            `4 ${am}`,
            "",
            `6 ${am}`,
            "",
            `8 ${am}`,
            "",
            `10 ${am}`,
            "",
            `12 ${pm}`,
            "",
            `2 ${pm}`,
            "",
            `4 ${pm}`,
            "",
            `6 ${pm}`,
            "",
            `8 ${pm}`,
            "",
            `10 ${pm}`,
            ""
        ];
        this.legends = ["70dB", "80dB", "85dB", "90dB", "95dB", "100dB +"];
        this.legendColors = [
            "#444",
            "#f08262",
            "#f36c58",
            "#f36c58",
            "#ef5350",
            "#c73f3c"
        ];
        this.cardSpacingX = 3;
        this.cardSpacingY = 3;
        this.svg = null;
        this.dayLabels = null;
        this.timeLabels = null;
        this.legendCards = null;
        this.legendLabels = null;
        this.cards = null;
        this.tooltip = null;
        // [day hour dbs]
        this.chartData = {};
        this.tsFrom = 0;
        this.tsTo = 0;
        this.cData = [];
        this.lastReq = null;
    }
    watchtimeRange() {
        this.updateData();
    }
    watchprofileIds() {
        this.updateData();
    }
    watchsoundSource() {
        this.updateData();
    }
    get innerWidth() {
        return this.width - this.margin.left - this.margin.right;
    }
    get innerHeight() {
        return this.height - this.margin.top - this.margin.bottom;
    }
    get gridSizeX() {
        return Math.floor(this.innerWidth / this.days.length);
    }
    get gridSizeY() {
        return Math.floor(this.innerHeight / this.times.length);
    }
    get legendElementWidth() {
        return this.gridSizeX * 2;
    }
    get noDataAvailable() {
        if (this.loading)
            return false;
        return Object.keys(this.chartData).length == 0;
    }
    async mounted() {
        // [day hour dbs]
        for (var day = 0; day < 7; day++) {
            for (var hour = 0; hour < 24; hour++) {
                this.cData.push([day, hour, 0]);
            }
        }
        // await this.updateData()
        this.svg = d3
            .select(this.$refs.chart)
            .append("svg")
            .attr("viewBox", `0 0 ${this.width} ${this.height}`)
            .attr("width", "100%")
            .attr("height", "100%")
            .append("g");
        this.timeLabels = this.svg
            .selectAll(".timeLabel")
            .data(this.times)
            .enter()
            .append("text")
            .text((d) => d)
            .attr("x", this.innerWidth + this.margin.right / 2) // add simple offset
            .attr("y", (d, i) => this.margin.top + i * this.gridSizeY)
            .style("text-anchor", "end")
            .attr("transform", "translate(" + this.gridSizeY / 2 + ", -6)")
            .attr("class", "timeLabel mono axis");
        this.dayLabels = this.svg
            .selectAll(".dayLabel")
            .data(this.days)
            .enter()
            .append("text")
            .text((d) => d)
            .attr("x", (d, i) => this.margin.left + i * this.gridSizeX + this.gridSizeX / 2)
            .attr("y", this.innerHeight + this.margin.bottom / 5)
            .style("text-anchor", "middle")
            .attr("class", "dayLabel mono axis");
        this.legendLabels = this.svg
            .selectAll(".ledgend-label")
            .data(this.legends)
            .enter()
            .append("text")
            .text((d) => d)
            .attr("x", (d, i) => this.margin.left + (i + 1) * this.gridSizeX) // add simple offset
            .attr("y", this.innerHeight + this.margin.bottom)
            .style("text-anchor", "middle")
            .attr("class", "timeLabel mono axis");
        this.legendCards = this.svg
            .selectAll(".ledgend-label")
            .data(this.legends)
            .enter()
            .append("rect")
            .attr("x", (d, i) => this.margin.left + (i + 1) * this.gridSizeX) // add simple offset
            .attr("y", this.innerHeight + (this.margin.bottom / 5) * 3)
            .attr("width", this.gridSizeX - this.cardSpacingX)
            .attr("height", this.gridSizeY - this.cardSpacingY)
            .style("fill", (d, i) => this.legendColors[i]);
        this.tooltip = d3.select(this.$refs.tooltip);
        this.updateData();
    }
    async updateData() {
        const that = this;
        var svg = this.svg;
        svg.selectAll(".hour").remove();
        // track last request
        this.lastReq = {};
        const lastReqLocal = this.lastReq;
        this.loading = true;
        let data = null;
        try {
            data = await dataService.getHistogramData({
                tz: this.tzName,
                source: this.soundSource,
                // profiles: this.profileIds,
                from_millis: this.timeRange.min,
                to_millis: this.timeRange.max
            });
        }
        catch (err) {
            this.setLastErr("Could not load Data: " + err);
            return;
        }
        // only let latest request pass
        if (this.lastReq !== lastReqLocal)
            return;
        const days = {};
        for (let v of data.Items) {
            // filter selected profile ids
            if (!this.profileIds.find((pid) => pid == v.profile_id))
                continue;
            // create map
            let dow = v.dow - 1;
            days[dow] = days[dow] || {};
            days[dow][v.hod] = days[dow][v.hod] || {
                cnt: 0,
                dba: 0,
                measurementCount: 0
            };
            days[dow][v.hod].cnt++;
            days[dow][v.hod].dba += v.dba;
            days[dow][v.hod].measurementCount += v.measurement_count;
        }
        this.chartData = days;
        let dta = [];
        for (var day = 0; day < 7; day++) {
            for (var hour = 0; hour < 24; hour++) {
                let dba = 0, cnt = 0;
                if (this.chartData[day] && hour in this.chartData[day]) {
                    let slot = this.chartData[day][hour];
                    dba = slot.dba / slot.cnt;
                    cnt = slot.measurementCount;
                }
                dta.push([day, hour, dba, cnt]);
            }
        }
        var cards = svg
            .selectAll(".hour")
            .data(dta, (d) => d[2])
            .enter()
            .append("rect")
            .attr("x", (d) => this.margin.left + d[0] * this.gridSizeX + this.cardSpacingX / 2)
            .attr("y", (d) => this.margin.top +
            d[1] * this.gridSizeY -
            this.gridSizeY * 2 +
            this.cardSpacingY / 2)
            .attr("class", "hour")
            .attr("width", this.gridSizeX - this.cardSpacingX)
            .attr("height", this.gridSizeY - this.cardSpacingY)
            .style("fill", (d) => this.colorScale(d))
            // // tooltip broken!!??
            .on("mouseover", function (d) {
            // d3.select(this).style('fill', that.colorScale(d[2]-20))
            if (!d[2])
                return;
            that.tooltip.style("visibility", "visible");
            that.tooltip.html(Math.round(d[2]) + " dBA");
        })
            .on("mouseout", function (d) {
            // d3.select(this).style('fill', that.colorScale(d[2]))
            that.tooltip.style("visibility", "hidden");
        })
            .on("mousemove", function (d) {
            that.tooltip
                .style("top", d3.event.pageY + "px")
                .style("left", d3.event.pageX + "px");
        });
        cards.exit().remove();
        this.loading = false;
    }
    colorScale(val) {
        let v = val[2] || 0;
        if (!v)
            return "#000";
        if (v < 70)
            return this.legendColors[0];
        if (v < 80)
            return this.legendColors[1];
        if (v < 85)
            return this.legendColors[2];
        if (v < 90)
            return this.legendColors[3];
        if (v < 100)
            return this.legendColors[5];
        return this.legendColors[5];
    }
};
tslib_1.__decorate([
    Getter("tzName")
], widget.prototype, "tzName", void 0);
tslib_1.__decorate([
    Getter("config/selectedSoundSource")
], widget.prototype, "soundSource", void 0);
tslib_1.__decorate([
    Getter("config/histogramTimeRange")
], widget.prototype, "timeRange", void 0);
tslib_1.__decorate([
    Getter("config/profileIds")
], widget.prototype, "profileIds", void 0);
tslib_1.__decorate([
    Getter("auth/profileByID")
], widget.prototype, "profileByID", void 0);
tslib_1.__decorate([
    Mutation
], widget.prototype, "setLastErr", void 0);
tslib_1.__decorate([
    Watch("timeRange", { deep: true })
], widget.prototype, "watchtimeRange", null);
tslib_1.__decorate([
    Watch("profileIds", { deep: true })
], widget.prototype, "watchprofileIds", null);
tslib_1.__decorate([
    Watch("soundSource", { deep: true })
], widget.prototype, "watchsoundSource", null);
widget = tslib_1.__decorate([
    Component({
        components: {
            loader,
            "select-time-range": selectTimeRange,
            "no-data": noDataOverlay
        }
    })
], widget);
export default widget;
